import * as Sentry from "@sentry/react";
import { detectEnvironment, toDeploymentEnvironment } from "@zektec/environment-utils";
import { env, general } from "../../Consts/config";
import { useEffect } from "react";
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from "react-router-dom";

const APP_VERSION = import.meta.env.VITE_APP_VERSION || "development";

const parseSampleRate = (value: unknown, fallback: number) => {
	const parsed =
		typeof value === "string" ? Number.parseFloat(value) : Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

const isHeadlessUserAgent = (event: Sentry.Event) => {
	const request = event.request;
	const headers = request && request.headers;
	const userAgent = headers && headers["User-Agent"];
	return userAgent?.toLowerCase().includes("headless");
};

const isFirebaseInstallationsNetworkError = (event: Sentry.Event) => {
	const exceptionValues = event.exception?.values || [];
	const hasInstallationsException = exceptionValues.some((exception) => {
		const value = exception.value || "";
		return (
			exception.type === "TypeError" &&
			value.includes("NetworkError") &&
			value.includes("firebaseinstallations.googleapis.com")
		);
	});

	const hasInstallationsBreadcrumb = event.breadcrumbs?.some((breadcrumb) => {
		const url =
			typeof breadcrumb.data?.url === "string" ? breadcrumb.data.url : "";
		return url.includes("firebaseinstallations.googleapis.com");
	});

	return hasInstallationsException || Boolean(hasInstallationsBreadcrumb);
};

const isViteCssPreloadError = (event: Sentry.Event) => {
	const exceptionValues = event.exception?.values || [];

	return exceptionValues.some((exception) => {
		const value = exception.value || "";
		const stackFrames = exception.stacktrace?.frames || [];
		const rawStackFrames = exception.rawStacktrace?.frames || [];
		const frames = [...stackFrames, ...rawStackFrames];
		const hasPreloadHelperFrame = frames.some((frame) => {
			const filename = frame.filename || frame.absPath || "";
			return filename.includes("preload-helper");
		});

		return (
			exception.type === "Error" &&
			value.startsWith("Unable to preload CSS for ") &&
			value.includes("/assets/") &&
			value.endsWith(".css") &&
			hasPreloadHelperFrame
		);
	});
};

const initSentry = () => {
	if (!env.SENTRY_DSN) return;

	const sentryEnvironment = toDeploymentEnvironment(
		detectEnvironment({
			mode: general.ENVIRONMENT,
			origin: window.location.origin,
		})
	);

	Sentry.init({
		dsn: env.SENTRY_DSN,
		release: APP_VERSION,
		integrations: [
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
			Sentry.replayIntegration({
				maskAllText: true,
				blockAllMedia: true,
			}),
		],
		tracesSampleRate: parseSampleRate(
			import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
			sentryEnvironment === "prod" ? 0.2 : 1
		),
		replaysSessionSampleRate: parseSampleRate(
			import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
			sentryEnvironment === "prod" ? 0.01 : 0.1
		),
		replaysOnErrorSampleRate: parseSampleRate(
			import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
			1
		),
		debug: false,
		environment: sentryEnvironment,
		sendDefaultPii: false,
		initialScope: {
			tags: {
				app: "landing-website",
				runtime: "browser",
			},
		},
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		beforeSend: (event, hint) => {
			if (isHeadlessUserAgent(event)) {
				console.info("Headless browser detected, not sending error to Sentry");
				return null;
			}
			if (isFirebaseInstallationsNetworkError(event)) {
				return null;
			}
			if (isViteCssPreloadError(event)) {
				return null;
			}
			if (event.user) delete event.user.email;
			return event;
		},
	});
};

const setSentryUser = (user: { uid?: string | null } | null) => {
	if (!user?.uid) {
		Sentry.setUser(null);
		Sentry.setTag("signed_in", "false");
		return;
	}

	Sentry.setUser({ id: user.uid });
	Sentry.setTag("signed_in", "true");
};

const addSentryRouteBreadcrumb = (path: string) => {
	Sentry.addBreadcrumb({
		category: "navigation",
		message: path,
		level: "info",
	});
	Sentry.setTag("route", path.split("?")[0] || "/");
};

export { addSentryRouteBreadcrumb, initSentry, setSentryUser };
export { isFirebaseInstallationsNetworkError, isViteCssPreloadError };
